import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Trash2 } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { AnnouncementModal } from '@/components/announcements/announcement-modal'
import { ResolutionModal } from '@/components/governance/resolution-modal'
import { useAuth } from '@/hooks/use-auth'
import { useCastVote, useDeletePoll, usePoll } from '@/hooks/use-polls'
import { getApiErrorMessage } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import { isCommitteeRole } from '@riverpark/types'

const STATUS_BADGE = {
  upcoming: { variant: 'pending', label: 'Upcoming' },
  open: { variant: 'paid', label: 'Open for voting' },
  closed: { variant: 'draft', label: 'Closed' },
} as const

export function PollDetailPage({ basePath }: { basePath: string }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data, isLoading } = usePoll(id)
  const castVote = useCastVote()
  const deletePoll = useDeletePoll()
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [showResolutionModal, setShowResolutionModal] = useState(false)
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false)

  const poll = data?.data
  const canManage = user ? isCommitteeRole(user.role) : false

  const backLink = (
    <Link
      to={basePath}
      className="inline-flex items-center gap-2 font-sans text-sm text-primary hover:text-green-lt"
    >
      <ArrowLeft className="size-4" aria-hidden />
      Back to governance
    </Link>
  )

  if (isLoading) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      </PageShell>
    )
  }

  if (!poll) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-destructive">Poll not found.</p>
        <div className="mt-4">{backLink}</div>
      </PageShell>
    )
  }

  const badge = STATUS_BADGE[poll.status]

  async function handleVote() {
    if (!poll || !selectedOption) return
    setError(null)
    try {
      await castVote.mutateAsync({ pollId: poll.id, optionId: selectedOption })
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to cast vote'))
    }
  }

  async function handleDelete() {
    if (!poll) return
    await deletePoll.mutateAsync(poll.id)
    navigate(basePath)
  }

  const maxVotes = Math.max(0, ...poll.options.map((o) => o.voteCount ?? 0))

  return (
    <PageShell>
      <PageHeader
        label="Governance"
        title={poll.title}
        description={`${poll.authorName} · ${formatDate(new Date(poll.createdAt * 1000))}`}
        action={backLink}
      />

      <div className="max-w-2xl space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={badge.variant}>{badge.label}</Badge>
          <span className="font-sans text-xs text-muted-foreground">
            {poll.votingUnit === 'one_plot_one_vote'
              ? 'One plot, one vote'
              : 'One member, one vote'}
          </span>
        </div>

        {poll.description && <p className="font-sans text-sm text-ink">{poll.description}</p>}

        <p className="font-sans text-xs text-muted-foreground">
          {poll.status === 'upcoming'
            ? `Opens ${formatDate(new Date(poll.startAt * 1000))}`
            : poll.status === 'open'
              ? `Closes ${formatDate(new Date(poll.endAt * 1000))}`
              : `Closed ${formatDate(new Date(poll.endAt * 1000))}`}
        </p>

        {poll.status === 'open' &&
          (poll.votedOptionId ? (
            <p className="rounded-card border border-border-2 bg-card p-4 font-sans text-sm text-ink">
              You voted for{' '}
              <span className="font-medium">
                {poll.options.find((o) => o.id === poll.votedOptionId)?.label}
              </span>
              . Results are hidden until the poll closes.
            </p>
          ) : (
            <div className="space-y-3 rounded-card border border-border-2 bg-card p-4">
              <div className="space-y-1.5">
                {poll.options.map((option) => (
                  <label
                    key={option.id}
                    className="flex items-center gap-2 rounded border border-border-2 px-3 py-2 font-sans text-sm text-ink hover:border-primary"
                  >
                    <input
                      type="radio"
                      name="poll-option"
                      checked={selectedOption === option.id}
                      onChange={() => setSelectedOption(option.id)}
                      className="size-4"
                    />
                    {option.label}
                  </label>
                ))}
              </div>
              <Button onClick={handleVote} loading={castVote.isPending} disabled={!selectedOption}>
                Cast vote
              </Button>
            </div>
          ))}

        {poll.status === 'closed' && (
          <div className="space-y-2.5 rounded-card border border-border-2 bg-card p-4">
            {poll.options.map((option) => {
              const count = option.voteCount ?? 0
              const pct = poll.totalVotes > 0 ? Math.round((count / poll.totalVotes) * 100) : 0
              const isWinner = poll.totalVotes > 0 && count === maxVotes
              return (
                <div key={option.id} className="space-y-1">
                  <div className="flex items-center justify-between font-sans text-sm">
                    <span className={isWinner ? 'font-semibold text-ink' : 'text-ink'}>
                      {option.label}
                    </span>
                    <span className="text-muted-foreground">
                      {count} · {pct}%
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-cream-2">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )
            })}
            <p className="font-sans text-xs text-muted-foreground">{poll.totalVotes} total votes</p>
          </div>
        )}

        {poll.status === 'upcoming' && (
          <p className="rounded-card border border-border-2 bg-card p-4 font-sans text-sm text-muted-foreground">
            Voting hasn't opened yet.
          </p>
        )}

        {error && (
          <p className="font-sans text-xs text-destructive" role="alert">
            {error}
          </p>
        )}

        {canManage && (
          <div className="flex flex-wrap gap-2 pt-2">
            {poll.status === 'closed' && (
              <Button variant="secondary" size="sm" onClick={() => setShowResolutionModal(true)}>
                Publish as resolution
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => setShowAnnouncementModal(true)}>
              Publish as announcement
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)}>
              <Trash2 size={14} className="mr-1.5" />
              Delete poll
            </Button>
          </div>
        )}
      </div>

      {showResolutionModal && (
        <ResolutionModal
          prefill={{ title: poll.title, pollId: poll.id }}
          onClose={() => setShowResolutionModal(false)}
        />
      )}

      {showAnnouncementModal && (
        <AnnouncementModal
          prefill={{
            title:
              poll.status === 'closed' ? `Poll results: ${poll.title}` : `Vote now: ${poll.title}`,
            body:
              poll.status === 'closed'
                ? `<p>The poll "${poll.title}" has closed. See the results below.</p>`
                : `<p>A new poll is open for voting: "${poll.title}". Cast your vote before it closes.</p>`,
            category: 'governance',
            link: { entityType: 'poll', entityId: poll.id },
          }}
          onClose={() => setShowAnnouncementModal(false)}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-navy/40 p-4">
          <div className="w-full max-w-sm rounded-card border border-border-2 bg-card p-6">
            <h2 className="font-serif text-lg text-ink">Delete poll?</h2>
            <p className="mt-1 font-sans text-sm text-muted-foreground">
              "{poll.title}" and its votes will be permanently removed. This can't be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
              <Button variant="destructive" loading={deletePoll.isPending} onClick={handleDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  )
}
